import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, Activity, Network, Archive, Settings, ScrollText, ShieldAlert, MonitorSmartphone, ExternalLink } from 'lucide-react'
import clsx from 'clsx'
import { useManagedDevice } from '../../hooks/useManagedDevices'
import { useManagedDeviceTypes } from '../../hooks/useManagedDeviceTypes'
import { managedDevicesApi } from '../../services/managedDevicesApi'
import DeviceStatusBadge from '../../components/DeviceStatusBadge'
import DeviceKindIcon from '../../components/DeviceKindIcon'
import MetricsHistorySection from './components/sections/MetricsHistorySection'
import BootstrapSection from './components/sections/BootstrapSection'
import RemoteNetworksSection from './components/sections/RemoteNetworksSection'
import VpnLinksSection from './components/sections/VpnLinksSection'
import RoutesSection from './components/sections/RoutesSection'
import FirewallSection from './components/sections/FirewallSection'
import EditDeviceSection from './components/sections/EditDeviceSection'
import DangerZoneSection from './components/sections/DangerZoneSection'
import BackupSection from './components/sections/BackupSection'
import LogsSection from './components/sections/LogsSection'
import SecuritySection from './components/sections/SecuritySection'
import OtpLoginSection from './components/sections/OtpLoginSection'
import AppScreenSection from './components/sections/AppScreenSection'
import { openManagedDeviceWebUiWindow } from '../../utils/openDeviceWebUiWindow'
import MobileDeviceConsole from './mobile/MobileDeviceConsole'
import { isMobileDevice } from '../../services/mdmApi'

const TABS = [
  { id: 'metrics', icon: Activity, labelKey: 'managedDeviceDetail.tabs.metrics', labelDefault: 'Métricas' },
  { id: 'network', icon: Network, labelKey: 'managedDeviceDetail.tabs.network', labelDefault: 'Rede + Firewall' },
  { id: 'logs', icon: ScrollText, labelKey: 'managedDeviceDetail.tabs.logs', labelDefault: 'Logs' },
  { id: 'security', icon: ShieldAlert, labelKey: 'managedDeviceDetail.tabs.security', labelDefault: 'Segurança' },
  { id: 'backup', icon: Archive, labelKey: 'managedDeviceDetail.tabs.backup', labelDefault: 'Backup' },
  { id: 'app', icon: MonitorSmartphone, labelKey: 'managedDeviceDetail.tabs.app', labelDefault: 'Tela + Sync', hostOnly: true },
  { id: 'data', icon: Settings, labelKey: 'managedDeviceDetail.tabs.data', labelDefault: 'Dados + Bootstrap' },
]

export default function ManagedDeviceDetail() {
  const { deviceId } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { data: device, isLoading, error } = useManagedDevice(deviceId)
  const { data: deviceTypes } = useManagedDeviceTypes()
  const [activeTab, setActiveTab] = useState('metrics')

  // Volta para a aba da lista do MESMO tipo do device (router -> lista de
  // routers, hosts -> lista de hosts). A lista lê `?type=<slug>` (default
  // 'hosts'), então sem o slug o "voltar" caía sempre na lista de hosts.
  const goBackToList = () => {
    const slug = device && deviceTypes
      ? deviceTypes.find(dt =>
          (dt.kindDefinitions || []).some(k => k.slug === device.kind))?.slug
      : null
    navigate(slug ? `/managed-devices?type=${slug}` : '/managed-devices')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('managedDeviceDetail.loading', 'Carregando dispositivo...')}
      </div>
    )
  }

  if (error || !device) {
    return (
      <div className="p-6">
        <button onClick={goBackToList} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-4 h-4" />
          {t('common.buttons.back', 'Voltar')}
        </button>
        <p className="text-sm text-red-600">{t('managedDeviceDetail.notFound', 'Dispositivo não encontrado.')}</p>
      </div>
    )
  }

  // Tablets/celulares do MDM (tipo "mobile") têm console próprio.
  if (isMobileDevice(device)) {
    return <MobileDeviceConsole device={device} onBack={goBackToList} />
  }

  // Determinar deviceType para reuso dos services existentes (router|host)
  const deviceType = managedDevicesApi.isRouter(device) ? 'router' : 'host'

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-6xl mx-auto">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <button
          onClick={goBackToList}
          className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 mb-3"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {t('managedDeviceDetail.backToList', 'Voltar para lista')}
        </button>

        <div className="flex flex-wrap items-center gap-3">
          <DeviceKindIcon
            kind={device.kind}
            kindIconFileName={device.kindIconFileName}
            customIconBase64={device.customIconBase64}
            size={20}
          />
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold text-gray-900 truncate">{device.name}</h1>
            {device.description && (
              <p className="text-sm text-gray-500 truncate">{device.description}</p>
            )}
          </div>
          {device.templateId && (
            <button
              type="button"
              onClick={() => openManagedDeviceWebUiWindow(device.id)}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-theme-primary text-white"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {t('managedDeviceDetail.openScreen', 'Abrir tela')}
            </button>
          )}
          <DeviceStatusBadge status={device.status} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs">
          <Field label="Kind" value={device.kindName || device.kind} />
          <Field label="VPN IP" value={device.vpnIp || '—'} mono />
          <Field label="Tunnel IP" value={device.vpnTunnelIp || '—'} mono />
          <Field label="Latência" value={device.latency != null ? `${device.latency}ms` : '—'} />
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6 overflow-x-auto">
          {TABS.filter((tab) => (!tab.routerOnly || deviceType === 'router') && (!tab.hostOnly || deviceType === 'host')).map(({ id, icon: Icon, labelKey, labelDefault }) => {
            const isActive = activeTab === id
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                className={clsx(
                  'pb-3 text-sm font-medium border-b-2 transition-colors inline-flex items-center whitespace-nowrap',
                  isActive
                    ? 'tab-active-secondary'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )}
              >
                <Icon className={clsx('w-4 h-4 inline mr-2', isActive ? 'text-theme-secondary' : 'text-gray-400')} />
                {t(labelKey, labelDefault)}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'metrics' && (
        <MetricsHistorySection deviceId={device.id} currentMetricsJson={device.metricsJson} />
      )}

      {activeTab === 'network' && (
        <div className="space-y-4">
          <RemoteNetworksSection device={device} deviceType={deviceType} />
          {deviceType === 'router' && <VpnLinksSection device={device} />}
          <RoutesSection device={device} deviceType={deviceType} />
          <FirewallSection device={device} />
        </div>
      )}

      {activeTab === 'logs' && (
        <LogsSection device={device} />
      )}

      {activeTab === 'security' && (
        <div className="space-y-4">
          <OtpLoginSection device={device} />
          {deviceType === 'router' && <SecuritySection device={device} />}
        </div>
      )}

      {activeTab === 'backup' && (
        <BackupSection device={device} />
      )}

      {activeTab === 'app' && (
        <AppScreenSection device={device} />
      )}

      {activeTab === 'data' && (
        <div className="space-y-4">
          <EditDeviceSection device={device} deviceType={deviceType} />
          <BootstrapSection device={device} />
          <DangerZoneSection device={device} />
        </div>
      )}
    </div>
  )
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`text-gray-900 truncate ${mono ? 'font-mono' : ''}`}>{value ?? '—'}</div>
    </div>
  )
}
